import { LitElement, css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import closeSvg from '../assets/close.svg'

/**
 * Mirrors SummaryTelemetry.MaxFeedbackLength on the API, which rejects anything longer. Capping
 * the input here is what keeps that rejection from ever happening — without it long feedback is
 * discarded server-side while the user is still shown the success toast.
 */
const MAX_FEEDBACK_LENGTH = 4000;

/** Show the remaining-character count only once the limit is actually in sight. */
const COUNTER_VISIBLE_FROM = MAX_FEEDBACK_LENGTH - 500;

@customElement('facs-feedback-modal')
export class FeedbackModal extends LitElement {
    @property({ type: Boolean })
    open = false;

    @state()
    private feedbackText = '';

    private handleOverlayClick(e: MouseEvent) {
        if (e.target === e.currentTarget) this.close();
    }

    private close() {
        this.dispatchEvent(new CustomEvent('modal-close', { bubbles: true, composed: true }));
    }

    private handleSubmit() {
        this.dispatchEvent(new CustomEvent('modal-submit', {
            detail: { feedback: this.feedbackText },
            bubbles: true,
            composed: true
        }));
        this.feedbackText = '';
        this.close();
    }

    render() {
        if (!this.open) return html``;
        return html`
            <div class="overlay" @click=${this.handleOverlayClick}>
                <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <button class="close-btn" @click=${this.close} aria-label="Close">
                        <img src="${closeSvg}" class="icon" alt="close" />
                    </button>
                    <h5 id="modal-title" class="title">Additional feedback</h2>
                    <p class="subtitle">Let us know more about your experience and we will work on it</p>
                    <textarea
                        id="feedback-textarea"
                        class="textarea"
                        placeholder="A short description of your experience"
                        .value=${this.feedbackText}
                        @input=${(e: Event) => this.feedbackText = (e.target as HTMLTextAreaElement).value}
                        maxlength=${MAX_FEEDBACK_LENGTH}
                        rows="5"
                    ></textarea>
                    ${this.feedbackText.length >= COUNTER_VISIBLE_FROM
                        ? html`<p class="counter" aria-live="polite">
                                   ${MAX_FEEDBACK_LENGTH - this.feedbackText.length} characters remaining
                               </p>`
                        : null}
                    <div class="actions">
                        <button class="submit-btn" @click=${this.handleSubmit} ?disabled=${!this.feedbackText.trim()}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    static styles = css`
        :host { display: contents; }

        .overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.45);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            animation: fadeIn 0.15s ease;
        }

        @keyframes fadeIn {
            from { opacity: 0; } to { opacity: 1; }
        }

        .modal {
            background: #fff;
            border-radius: 0.75rem;
            padding: 2rem;
            width: min(90vw, 31rem);
            position: relative;
            box-shadow: 0 1rem 2rem rgba(0,0,0,0.18);
            animation: slideUp 0.2s ease;
        }

        @keyframes slideUp {
            from { transform: translateY(1rem); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
        }

        .close-btn {
            all: unset;
            position: absolute;
            top: 1.25rem; right: 1.25rem;
            cursor: pointer;
            font-size: 1rem;
            font-weight: 600;
            color: #0F2144;
            transition: color 0.15s;
        }

        .close-btn:hover { color: #000; }

        .title {
            margin: 0 0 1rem;
            font-size: 1.375rem;
            line-height: 1.75rem;
            font-weight: 600;
            color: #0f2144;
        }

        .subtitle {
            margin: 0 0 1rem;
            font-size: 1rem;
            color: #555555;
        }

        .counter {
            margin: 0.25rem 0 0;
            font-size: 0.75rem;
            line-height: 1rem;
            text-align: right;
            color: #555;
        }

        .textarea {
            width: 100%;
            box-sizing: border-box;
            outline: none;
            font-family: inherit;
            transition: border-color 0.15s;
            border: 0.0625rem solid rgb(138, 163, 176);
            background: #fff;
            border-radius: 0.5rem;
            padding: 0.75rem;
            min-height: 10.25rem;
            font-size: 1rem;
            line-height: 1.75rem;
            color: #555555;
            height: 10.0625rem;
        }

        .textarea::placeholder {
            color: rgb(177, 177, 177);
        }

        .textarea:focus { border-color: #0f2144; background: #fff; }

        .actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 1.25rem;
        }

        .submit-btn {
            all: unset;
            background: #0F2144;
            color: #fff;
            cursor: pointer; 
            font-size: 1.125rem;
            line-height: 1.5rem;
            font-weight: 600;
            padding: 0.625rem 1.5rem;
            border-radius: 6.25rem;
            transition: background 0.2s;
        }

        .submit-btn:disabled {
            background: #B1B1B1;
            cursor: not-allowed;
        }
    `;
}